/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from 'lodash';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LuiSelect, LuiSelectItemEvent } from '@jda/lui-common-component-library-mui5';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { Button, TextField, Box, Checkbox, Typography, Grid, FormHelperText } from '@mui/material';

import useStyle from './styles';
import Loader from '../../shared/Loader';
import { createNewRole, updateRole } from '../../services/roles';
import {
  SAVE,
  NAME,
  CANCEL,
  MODULE,
  ADD_ROLE,
  EDIT_ROLE,
  DESCRIPTION,
  ORGANIZATION,
  APPLICATION,
  DISPLAY_NAME,
  CREATE_ROLE_BTN,
  MODULE_REQUIRED,
  DESCRIPTION_NAME,
  AdminRealmIdList,
  ROLE_NAME_REQUIRED,
  ROLE_CREATE_FAILED,
  GLOBAL_ROLE_CHECKBOX,
  ROLE_DESCRIPTION_REQUIRED,
  ROLE_IS_SAVED_SUCCESSFULLY,
  POLICY_TYPE_GLOBAL,
  APPLICATION_KEY,
  ORGANIZATION_KEY,
  MODULE_KEY,
  NAME_KEY,
} from './constant';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import FormDialog from '../../shared/Dialog/FormDialog';
import { DROPDOWN_MAX_HEIGHT } from '../UserTable/constants';
import interpolateString from '../../utility/stringInterpolation';
import { DROPDOWN_WIDTH_LARGE } from '../../shared/PolicyDialog/constants';
import { selectSelectedRoleItem } from '../../store/role/roleSelector';
import { IAddRoleDialogProps, IOrganization, IApplication, IModule } from '../../models/Roles/RolesContracts';
import { compareByProperty, getUserTokenRealmId } from '../../utility/commonUtility';
import { getApplicationListByOrg, getModuleListByApp, getOrganizationList } from '../../store/role/GlobalRole/globalRole';
import { selectAllApplicationList, selectAllModuleList, selectAllOrganizationList } from '../../store/role/GlobalRole/globalRoleSelector';

const convertToItemData = <T extends { id: number; name: string }>(data: T[]) => {
  const sortedData = [...data].sort(compareByProperty(NAME_KEY));

  return sortedData.map(item => ({ ...item, key: `${item.id}`, displayName: item.name }));
};

const AddRoleDialog = ({
  refreshOnSave,
  openModal,
  openCloseModal,
  hideSnackbar,
  displaySnackbar,
  edit
}: IAddRoleDialogProps) => {
  const { classes } = useStyle();
  const dispatch = useDispatch();

  const formDefaultValues = {
    name: '',
    description: '',
    moduleId: 0,
    type: "realm"
  };

  const formErrorDefaultValues = {
    error: false,
    errorHelperText: ''
  };

  const modules: IModule[] = useSelector(selectAllModuleList);
  const selectedItem = useSelector(selectSelectedRoleItem);
  const applications: IApplication[] = useSelector(selectAllApplicationList);
  const organizations: IOrganization[] = useSelector(selectAllOrganizationList);

  const organizationData = useMemo(() => convertToItemData(organizations), [organizations]);

  const applicationData = useMemo(() => convertToItemData(applications), [applications]);

  const moduleData = useMemo(() => convertToItemData(modules), [modules]);

  const [showLoader, showHideLoader] = useState(false);
  const [isGlobalRole, setIsGlobalRole] = useState(false);
  const [showGlobalRoleCreation, setShowGlobalRoleCreation] = useState(false);
  const [formValues, updateFormValues] = useState(formDefaultValues);
  const [globalRoleDetails, setGlobalRoleDetails] = useState<any>({
    organization: "",
    application: "",
    module: ""
  });
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formErrorModuleState, updateFormErrorModuleState] = useState(formErrorDefaultValues);
  const [formErrorStateDescription, updateFormErrorDescription] = useState(formErrorDefaultValues);

  const areGlobalRolesEnabled = useFeatureFlag(HARNESS_FEATURE_FLAG.globalRoles);

  const checkIsAdminRealm = async () => {
    const userRealmId = await getUserTokenRealmId();
    const realmFound = _.find(AdminRealmIdList, { realmId: userRealmId });
    if (realmFound) setShowGlobalRoleCreation(true);
  };

  useEffect(() => {
    checkIsAdminRealm();
  }, []);

  useEffect(() => {
    if (edit) {
      updateFormValues({
        name: selectedItem.name,
        description: selectedItem.description,
        moduleId: selectedItem.moduleId || 0,
        type: selectedItem.type || 'realm',
      });
    }
  }, [edit, selectedItem]);

  useEffect(() => {
    if (isGlobalRole) {
      getOrganizationList(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalRole, globalRoleDetails]);

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  const updateFormFields = (e: { target: { value: string } }) => {
    updateFormValues({ ...formValues, name: e.target.value });
    updateFormError(formErrorDefaultValues);
  };

  const updateFormFieldsDescription = (e: { target: { value: string } }) => {
    updateFormValues({ ...formValues, description: e.target.value });
    updateFormErrorDescription(formErrorDefaultValues);
  };
  const validateRequired = (values: any) => {
    if (
      values.name !== '' &&
      values.name !== undefined &&
      values.description !== '' &&
      values.description !== undefined &&
      (isGlobalRole ? globalRoleDetails.module.id : true)
    ) return true;
    else return false;
  };

  const handleSubmit = () => {
    formDefaultValues.name = formValues.name;
    formDefaultValues.description = formValues.description;
    if (isGlobalRole) {
      formDefaultValues.type = POLICY_TYPE_GLOBAL;
      formDefaultValues.moduleId = globalRoleDetails.module.id;
    }
    if (validateRequired(formDefaultValues)) {
      showHideLoader(true);
      if (!edit) {
        hideSnackbar();
        createNewRole(formDefaultValues).then(response => {
          if (response) {
            showHideLoader(false);
            refreshOnSave(response.name);
            displaySnackbar(interpolateString([response.name], ROLE_IS_SAVED_SUCCESSFULLY), 'success');
            openCloseModal(false);
          }
        }).catch((error: any) => {
          showHideLoader(false);
          displaySnackbar(interpolateString([error.message], ROLE_CREATE_FAILED), 'error');
        });
      } else {
        if (formDefaultValues.name !== '' || formDefaultValues.description !== '') {
          hideSnackbar();
          updateRole(formDefaultValues, selectedItem.id).then(() => {
            showHideLoader(false);
            displaySnackbar(interpolateString([formDefaultValues.name], ROLE_IS_SAVED_SUCCESSFULLY), 'success');
            openCloseModal(false);
            refreshOnSave(formDefaultValues.name);
          }).catch((error: any) => {
            showHideLoader(false);
            displaySnackbar(interpolateString([error.message], ROLE_CREATE_FAILED), 'error');
          });
        }
      }
    } else {
      if (formDefaultValues.name === '' || !formDefaultValues.name) {
        updateFormError({ ...formErrorState, error: true, errorHelperText: ROLE_NAME_REQUIRED });
      }
      if (formDefaultValues.description === '' || !formDefaultValues.description) {
        updateFormErrorDescription({
          ...formErrorStateDescription,
          error: true,
          errorHelperText: ROLE_DESCRIPTION_REQUIRED
        });
      }
      if (isGlobalRole && !globalRoleDetails.module.id) {
        updateFormErrorModuleState({
          ...formErrorModuleState,
          error: true,
          errorHelperText: MODULE_REQUIRED
        });
      }
    }
  };
  const onChangeGlobalRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsGlobalRole(event.target.checked);
  };

  const handleDropdownChange = (name: string, event: LuiSelectItemEvent) => {
    const selectedValue: any = event.target.value;
    if (name === ORGANIZATION_KEY) {
      setGlobalRoleDetails({ organization: selectedValue, module: '', application: '' });
      getApplicationListByOrg(dispatch, selectedValue.name);
    } else if (name === APPLICATION_KEY) {
      setGlobalRoleDetails({ ...globalRoleDetails, application: selectedValue, module: '' });
      getModuleListByApp(dispatch, selectedValue.name);
    } else {
      updateFormErrorModuleState(formErrorDefaultValues);
      setGlobalRoleDetails({ ...globalRoleDetails, [name]: selectedValue });
    };
  };

  return (
    <React.Fragment>
      <FormDialog
        maxWidth={'sm'}
        openModal={openModal}
        handleClose={handleClose}
        dialogTitle={edit ? EDIT_ROLE : ADD_ROLE}
        dialogAction1={
          <Button variant="outlined" name="cancelBtn" color="primary" disableElevation onClick={handleClose}>
            {CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            variant="contained"
            name={CREATE_ROLE_BTN}
            color="primary"
            disableElevation
            disabled={showLoader}
            onClick={handleSubmit}
          >
            {SAVE}
          </Button>
        }
      >
        <Box className={classes.modalFormContent}>
          <TextField
            autoFocus
            value={formValues.name}
            disabled={showLoader}
            fullWidth
            variant="standard"
            id="roleName"
            name={DISPLAY_NAME}
            label={NAME}
            className={classes.modalTextBox}
            defaultValue={formValues.name}
            onChange={updateFormFields}
            error={formErrorState.error}
            helperText={formErrorState.errorHelperText}
          />
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            fullWidth
            id="description"
            label={DESCRIPTION}
            variant="standard"
            disabled={showLoader}
            name={DESCRIPTION_NAME}
            value={formValues.description}
            className={classes.modalTextBox}
            defaultValue={formValues.description}
            onChange={updateFormFieldsDescription}
            error={formErrorStateDescription.error}
            helperText={formErrorStateDescription.errorHelperText}
          />
        </Box>
        {areGlobalRolesEnabled && showGlobalRoleCreation ?
          <Box className={classes.modalFormContent}>
            <Checkbox
              color='primary'
              checked={isGlobalRole}
              id={GLOBAL_ROLE_CHECKBOX}
              name={GLOBAL_ROLE_CHECKBOX}
              onChange={onChangeGlobalRole}
              data-testid={GLOBAL_ROLE_CHECKBOX}
            />
            <Typography variant='h4' className={classes.modalFormContent}>
              Global Role
              <Typography variant='h4' color='secondary'> (visible in entitled customer realms)</Typography>
            </Typography>
          </Box> : null
        }

        {isGlobalRole ?
          <Box className={classes.globalRoleWrap}>
            <Grid item xs={6} className={classes.dropDown}>
              <LuiSelect
                inputLabel={ORGANIZATION}
                id={"organization-dropdown"}
                width={DROPDOWN_WIDTH_LARGE}
                maxHeight={DROPDOWN_MAX_HEIGHT}
                selectedItem={globalRoleDetails.organization}
                itemData={organizationData}
                onSelectionChange={e => handleDropdownChange(ORGANIZATION_KEY, e)}
              />
            </Grid>
            <Grid item xs={6} className={classes.dropDown}>
              <LuiSelect
                id="application-dropdown"
                inputLabel={APPLICATION}
                width={DROPDOWN_WIDTH_LARGE}
                maxHeight={DROPDOWN_MAX_HEIGHT}
                disabled={!globalRoleDetails.organization}
                selectedItem={globalRoleDetails.application}
                itemData={applicationData}
                onSelectionChange={e => handleDropdownChange(APPLICATION_KEY, e)}
              />
            </Grid>
            <Grid item xs={6} className={classes.dropDown}>
              <LuiSelect
                inputLabel={MODULE}
                id="module-dropdown"
                width={DROPDOWN_WIDTH_LARGE}
                maxHeight={DROPDOWN_MAX_HEIGHT}
                selectedItem={globalRoleDetails.module}
                disabled={!globalRoleDetails.application}
                itemData={moduleData}
                onSelectionChange={e => handleDropdownChange(MODULE_KEY, e)}
              />
            </Grid>
            {formErrorModuleState.error ?
              <FormHelperText id="policyAction-helper-text" error>
                {formErrorModuleState.errorHelperText}
              </FormHelperText> : null
            }
          </Box> : null
        }
        <Loader showLoader={showLoader} />
      </FormDialog>
    </React.Fragment>
  );
};

export default AddRoleDialog;
